import { ChangeDetectorRef, Component, DestroyRef, ViewChild, computed, inject, model, signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { commonImports } from '../shared/table-interface';
import { MatRadioModule } from '@angular/material/radio';
import { MatCalendar, MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { JsonPipe, DatePipe } from '@angular/common';
import { HttpClient, HttpContext } from '@angular/common/http';
import { DynamicFormsV2Component } from '../shared/dynamic-forms-v2/dynamic-forms-v2.component';
import { AppointmentService } from '../services/appointment.service';
import { StateService } from '../shared/state.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ConsultationConfirmationDialogComponent } from './consultation-confirmation-dialog/consultation-confirmation-dialog.component';
import { EMPTY, forkJoin, map, of, catchError, Observable, switchMap, tap, expand, reduce } from 'rxjs';
import { HealthcareService, Practitioner, PractitionerRole, Schedule, Slot, Bundle } from 'fhir/r4';
import { backendEndPointToken } from '../app.config';
import { SuccessMessageComponent } from '../shared/success-message/success-message.component';
import { LoadingUIEnabled, SkipHospitalSegregation } from '../loading.interceptor';
import { AuthService } from '../shared/auth/auth.service';
import { ActivatedRoute } from '@angular/router';
import type { FormFields, ReferenceDataType, formMetaData, IndividualReferenceField } from '../shared/dynamic-forms.interface2';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SystemRoles } from '../shared/system-roles';
import { ReferralWorkflowService } from '../referral/services/referral-workflow.service';
import type { ReferralWorkflowInput, ReferralWorkflowResult } from '../referral/services/referral-workflow.models';


interface AvailableSlotItem {
  slot: Slot;
  practitionerName: string;
  timeDisplay: string;
  practitionerRef: string;
}

type ConsultationScope = 'same-org' | 'any-org';
type ConsultantOrganizationInfo = {
  organizationId?: string;
  organizationReference?: string;
  organizationDisplay?: string;
};

@Component({
  selector: 'app-bookings-form',
  imports: [...commonImports, ReactiveFormsModule,
    MatTimepickerModule, JsonPipe, DynamicFormsV2Component, DatePipe,
    MatRadioModule, MatDatepickerModule, MatDialogModule, MatSelectModule, MatFormFieldModule, MatInputModule],
  providers: [provideNativeDateAdapter()],
  templateUrl: './bookings-form.component.html',
  styleUrls: ['../create-schedule/create-schedule.component.scss', './bookings-form.component.scss']
})
export class BookingsFormComponent {
  @ViewChild(MatCalendar) private calendar?: MatCalendar<Date>;

  selected = model<Date | null>(null);
  http = inject(HttpClient);
  appointmentService = inject(AppointmentService);
  referralWorkflowService = inject(ReferralWorkflowService);
  state = inject(StateService);
  sn = inject(MatSnackBar);
  backendUrl = inject(backendEndPointToken);
  authService = inject(AuthService);
  dialog = inject(MatDialog);
  destroyRef = inject(DestroyRef);
  cdr = inject(ChangeDetectorRef);
  todaysDate = new Date();
  minDate = new Date();
  maxDate: Date | null = null;
  showSchedule = false;
  availableSlots: Array<AvailableSlotItem> = [];
  selectedSlot: AvailableSlotItem | null = null;
  loadingSlots = false;
  consultationMode: 'consult-appointment' | 'consult-only' | null = null;
  readonly isConsultationRequest = computed(() => this.bookingType().trim().toLowerCase() === 'consultation request');
  readonly consultationScope = signal<ConsultationScope>('same-org');
  bookingType = signal<string>('Standard Booking');
  
  deliveryMode = signal<'in-person' | 'virtual'>('virtual');
  isWaitlist = signal<boolean>(false);
  
  preferredDate = signal<Date | null>(null);
  preferredTime = signal<string>('');
  minutesDuration = signal<number>(30);
  patientInstruction = signal<string>('');

  readonly isVirtualBooking = computed(() => this.deliveryMode() === 'virtual' || this.bookingType().trim().toLowerCase() === 'virtual');
  readonly showServiceSelection = computed(() => !this.isConsultationRequest());
  readonly defaultServiceOption = 'telemed:general';
  readonly otherServiceOption = 'telemed:other';
  serviceOptions: Array<{ value: string; label: string; specialtyCode?: string; specialtyDisplay?: string; primarySpecialtyName?: string }> = [
    { value: this.defaultServiceOption, label: 'General Consultation' },
    { value: this.otherServiceOption, label: 'Other Services' }
  ];
  selectedService = signal<string>(this.defaultServiceOption);
  loadingServicePractitioners = false;
  
  assignMode = signal<boolean>(false);
  existingAppointment = signal<any>(null);
  
  private servicePractitionerCache = new Map<string, Set<string>>();
  loadingOrgPractitioners = false;
  private orgPractitionerCache = new Map<string, Set<string>>();
  // Options for the dynamic-form practitioner picker (derived from schedules)
  practitionerReferenceOptions: ReferenceDataType[] = [];
  private basePractitionerReferenceOptions: ReferenceDataType[] = [];
  practitionerPickerFields: FormFields[] = [];
  practitionerPickerMeta: formMetaData | undefined;
  selectedPractitionerRef: string | null = null;

  selectedPractitioner: Practitioner | null = null;
  selectedPractitionerPhone: string | null = null;
  selectedPractitionerEmail: string | null = null;
  readonly consultationNote = signal('');

  sameOrgConsultantsOptions: ReferenceDataType[] = [];
  globalConsultantsOptions: ReferenceDataType[] = [];
  loadingSameOrgConsultants = false;
  loadingGlobalConsultants = false;

  // Cache schedules to resolve practitioner references
  activeSchedules: Schedule[] = [];

  // Chip-based schedule selection
  selectedScheduleId: string | null = null;

  // Schedules for the currently selected practitioner (shown as chips)
  get schedulesForSelectedPractitioner(): Schedule[] {
    const ref = (this.selectedPractitionerRef ?? '').trim();
    if (!ref) return [];
    return this.activeSchedules.filter((s) => {
      const actors = (s.actor ?? []) as Array<{ reference?: string }>;
      return actors.some((a) => String(a?.reference ?? '') === ref);
    });
  }

  freeDatesSet: Set<string> = new Set();
  calendarStartAt: Date = new Date();

  calendarDateClass = this.getCalendarDateClass();

  getCalendarDateClass() {
    return (date: Date): string => {
      const localIso = date.toLocaleDateString('en-CA');
      return this.freeDatesSet.has(localIso) ? 'available-date' : '';
    };
  }

  private refreshCalendarHighlights(): void {
    this.calendarDateClass = this.getCalendarDateClass();
    this.cdr.detectChanges();

    queueMicrotask(() => {
      this.calendar?.updateTodaysDate();
      (this.calendar as any)?.stateChanges?.next();
    });
  }

  dialogData = inject(MAT_DIALOG_DATA, { optional: true });

  ngOnInit() {
    // Read default booking type from route data (e.g., 'Telemedicine' for patient booking route) or from dialog data.
    const defaultType = this.route.snapshot.data?.['defaultBookingType'] || this.dialogData?.defaultBookingType;
    if (defaultType) {
      this.bookingType.set(defaultType);
    }

    // If the logged-in user is a Patient, ensure we always have a usable patient context
    // even when this component is not mounted under /app/patients/:id (and its resolver).
    this.seedPatientContextFromLoggedInUser();

    if (this.dialogData?.appointment) {
      this.assignMode.set(true);
      this.existingAppointment.set(this.dialogData.appointment);
      this.isWaitlist.set(false); // Force manual slot selection in assign mode
      
      const appt = this.dialogData.appointment;
      // Extract preferred date from requestedPeriod if available
      const prefStart = appt.requestedPeriod?.[0]?.start;
      if (prefStart) {
        this.preferredDate.set(new Date(prefStart));
        this.selected.set(new Date(prefStart));
      }
      
      if (appt.patientInstruction) {
        this.patientInstruction.set(appt.patientInstruction);
      }
    }

    this.observeHealthcareServices();
    this.updateShowSchedule();
    this.fetchActiveSchedules();
    this.fetchSameOrgConsultants();
    this.fetchGlobalConsultants();
    
    // Set consultationMode from dialog data if available
    if (this.dialogData?.consultationMode) {
      this.consultationMode = this.dialogData.consultationMode;
    }
  }

  getRequestedPeriodStart(): string | undefined {
    const date = this.isWaitlist() ? this.preferredDate() : this.selected();
    if (!date) return undefined;
    const iso = date.toISOString().split('T')[0];
    return `${iso}T08:00:00Z`; // Default to 8am for waitlist/general requests
  }

  getRequestedPeriodEnd(): string | undefined {
    const date = this.isWaitlist() ? this.preferredDate() : this.selected();
    if (!date) return undefined;
    const iso = date.toISOString().split('T')[0];
    return `${iso}T18:00:00Z`; // Default to 6pm
  }

  fetchActiveSchedules() {
    // Fetch all active schedules to know who is available
    this.http.get<Bundle<Schedule>>(`${this.backendUrl}/Schedule?active=true&_count=200`, {
      context: this.consultantReadContext()
    }).subscribe({
      next: (bundle) => {
        this.activeSchedules = (bundle.entry || []).map(e => e.resource as Schedule).filter(Boolean);
        console.log('[BookingsForm] Active schedules fetched:', this.activeSchedules.length, this.activeSchedules);
        this.buildPractitionerListFromSchedules();

        // If a practitioner is already selected, recompute free slots.
        if (this.selectedPractitionerRef) {
          this.fetchAndHighlightFreeSlots(this.selectedPractitionerRef, this.selectedScheduleId);
        }
      },
      error: (err) => {
        console.error('[BookingsForm] Failed to fetch schedules', err);
      }
    });
  }

  private observeHealthcareServices(): void {
    this.state.orgWideResources.healthcareServices
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((entries) => this.updateTelemedicineServiceOptions(entries || []));
  }

  private updateTelemedicineServiceOptions(entries: Array<{ actualResource: HealthcareService }>): void {
    const orgId = this.authService.user.getValue()?.organizationId?.trim();
    const orgRef = orgId ? `Organization/${orgId}` : null;

    // Preload practitioner scope for this organization (used for patient filtering).
    if (orgId) {
      this.fetchPractitionersForOrganization(orgId);
    }

    const serviceOptions = (entries || [])
      .map((entry) => entry?.actualResource)
      .filter((svc): svc is HealthcareService => Boolean(svc?.id))
      .filter((svc) => svc.active !== false)
      .filter((svc) => !orgRef || svc.providedBy?.reference === orgRef)
      .map((svc) => {
        const specialtyList = (svc.specialty ?? [])
          .map(s => s.coding?.[0]?.display || s.text)
          .filter(Boolean)
          .join(', ');
        
        const firstSpecialty = svc.specialty?.[0];
        const primarySpecialtyName = firstSpecialty?.coding?.[0]?.display || firstSpecialty?.text;
        const specialtyCode = firstSpecialty?.coding?.[0]?.code;

        return {
          value: `HealthcareService/${svc.id}`,
          label: svc.name?.trim() || svc.type?.[0]?.text || `Healthcare Service ${svc.id}`,
          specialtyDisplay: specialtyList || undefined,
          primarySpecialtyName: primarySpecialtyName || undefined,
          specialtyCode: specialtyCode || undefined
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    const merged = [
      { value: this.defaultServiceOption, label: 'General Consultation' },
      ...serviceOptions,
      { value: this.otherServiceOption, label: 'Other Services' }
    ];

    this.serviceOptions = merged.filter((option, index, arr) => index === arr.findIndex((o) => o.value === option.value));

    if (!this.serviceOptions.some((option) => option.value === this.selectedService())) {
      this.selectedService.set(this.defaultServiceOption);
    }

    this.applyPractitionerFilters();
  }

  onServiceChange(value: string): void {
    const nextValue = value || this.defaultServiceOption;
    this.selectedService.set(nextValue);

    if (this.selectedPractitionerRef) {
      this.selectPractitioner(null);
    }

    if (!this.showServiceSelection()) {
      this.loadingServicePractitioners = false;
      this.applyPractitionerFilters();
      return;
    }

    if (!this.isHealthcareServiceReference(nextValue)) {
      this.loadingServicePractitioners = false;
      this.applyPractitionerFilters();
      return;
    }

    if (this.servicePractitionerCache.has(nextValue)) {
      this.loadingServicePractitioners = false;
      this.applyPractitionerFilters();
      return;
    }

    this.loadingServicePractitioners = true;
    this.practitionerReferenceOptions = [];
    this.updatePractitionerPickerField([]);
    this.fetchPractitionersForService(nextValue);
  }

  private fetchPractitionersForService(serviceRef: string): void {
    const id = this.extractHealthcareServiceId(serviceRef);
    if (!id) {
      this.servicePractitionerCache.set(serviceRef, new Set());
      this.loadingServicePractitioners = false;
      this.applyPractitionerFilters();
      return;
    }

    const ctx = new HttpContext().set(LoadingUIEnabled, false);
    const url = `${this.backendUrl}/PractitionerRole?service=HealthcareService/${encodeURIComponent(id)}&_count=200`;
    this.http.get<Bundle<PractitionerRole>>(url, { context: ctx }).subscribe({
      next: (bundle) => {
        const allowedRefs = new Set<string>();
        for (const entry of bundle?.entry ?? []) {
          const role = entry?.resource as PractitionerRole | undefined;
          const practitionerRef = role?.practitioner?.reference?.trim();
          if (practitionerRef && practitionerRef.includes('Practitioner/')) {
            allowedRefs.add(practitionerRef);
          }
        }
        this.servicePractitionerCache.set(serviceRef, allowedRefs);
        this.loadingServicePractitioners = false;
        this.applyPractitionerFilters();
      },
      error: (err) => {
        console.error('Failed to fetch PractitionerRoles for service', err);
        this.servicePractitionerCache.set(serviceRef, new Set());
        this.loadingServicePractitioners = false;
        this.applyPractitionerFilters();
      }
    });
  }

  private fetchPractitionersForOrganization(orgId: string): void {
    const normalizedOrgId = this.normalizeOrganizationId(orgId);
    if (!normalizedOrgId) return;

    if (this.orgPractitionerCache.has(normalizedOrgId) || this.loadingOrgPractitioners) {
      return;
    }

    this.loadingOrgPractitioners = true;
    const ctx = new HttpContext().set(LoadingUIEnabled, false).set(SkipHospitalSegregation, true);

    const url = `${this.backendUrl}/PractitionerRole?organization=Organization/${encodeURIComponent(normalizedOrgId)}&_count=200`;

    const extractRefs = (bundle: Bundle<PractitionerRole> | any): Set<string> => {
      const allowedRefs = new Set<string>();
      for (const entry of bundle?.entry ?? []) {
        const role = entry?.resource as PractitionerRole | undefined;
        const practitionerRef = role?.practitioner?.reference?.trim();
        if (practitionerRef && practitionerRef.includes('Practitioner/')) {
          allowedRefs.add(practitionerRef);
        }
      }
      return allowedRefs;
    };

    this.http.get<Bundle<PractitionerRole>>(url, { context: ctx }).pipe(
      catchError(() => of(null))
    ).subscribe({
      next: (bundle) => {
        this.orgPractitionerCache.set(normalizedOrgId, extractRefs(bundle));
        this.loadingOrgPractitioners = false;
        this.applyPractitionerFilters();
      },
      error: (err) => {
        console.error('Failed to fetch PractitionerRoles for organization', err);
        this.orgPractitionerCache.set(normalizedOrgId, new Set());
        this.loadingOrgPractitioners = false;
        this.applyPractitionerFilters();
      }
    });
  }

  private applyPractitionerFilters(): void {
    if (this.bookingType() === 'Consultation Request') {
      const baseOptions = this.consultationScope() === 'any-org'
        ? this.globalConsultantsOptions
        : this.sameOrgConsultantsOptions;
      const filteredOptions = this.filterOutCurrentPractitioner(baseOptions);
      this.practitionerReferenceOptions = filteredOptions;
      this.updatePractitionerPickerField(filteredOptions);
      this.ensureSelectedPractitionerStillValid();
      return;
    }

    let filtered = [...this.basePractitionerReferenceOptions];

    const currentUser = this.authService.user.getValue();
    const isPatientUser = this.isPatientOnlyUser(currentUser);
    const orgId = (currentUser?.organizationId ?? '').toString().trim();

    // Patient users should only see practitioners belonging to their organization.
    // This applies for both Telemedicine and Consultation Request flows.
    if (isPatientUser && orgId) {
      console.log(filtered, 'before filtering by orgPrac')
      const allowedByOrg = this.orgPractitionerCache.get(orgId);
      if (allowedByOrg) {
        filtered = filtered.filter((option) => allowedByOrg.has(String(option.reference ?? '')));
         console.log(filtered, allowedByOrg, 'there are orgPract')
      } else {
        this.fetchPractitionersForOrganization(orgId);
        console.log('will be renetering this funvtio')
      }
    }
    console.log("did i really start over");

    if (this.showServiceSelection() && this.isHealthcareServiceReference(this.selectedService())) {
      const allowed = this.servicePractitionerCache.get(this.selectedService()) ?? new Set<string>();
      filtered = filtered.filter((option) => allowed.has(String(option.reference ?? '')));
    }

    this.practitionerReferenceOptions = filtered;
    console.log('[BookingsForm] Final practitioner options after filtering:', filtered.length, filtered);
    this.updatePractitionerPickerField(filtered);
    this.ensureSelectedPractitionerStillValid();
  }

  private seedPatientContextFromLoggedInUser(): void {
    const user = this.authService.user.getValue();
    if (!this.isPatientOnlyUser(user)) return;

    const patientId = this.normalizePatientId(user?.['fhirResourceId']);
    if (!patientId) return;

    // Let StateService derive currentPatientId$ from this resolver-like source.
    this.state.currentPatientIdFromResolver.next(patientId);
  }

  private getEffectivePatientId(): string | null {
    const fromState = this.normalizePatientId(this.state.currentPatientId$.getValue());
    if (fromState) return fromState;

    const user = this.authService.user.getValue();
    if (this.isPatientOnlyUser(user)) {
      const fromUser = this.normalizePatientId(user?.['fhirResourceId']);
      if (fromUser) {
        // Seed state for downstream consumers.
        this.state.currentPatientIdFromResolver.next(fromUser);
        return fromUser;
      }
    }

    return null;
  }

  private normalizePatientId(value: any): string | null {
    const raw = (value ?? '').toString().trim();
    if (!raw) return null;
    const withoutQuery = raw.split('?')[0];
    const parts = withoutQuery.split('/').filter(Boolean);
    return parts.length ? parts[parts.length - 1] : null;
  }

  private normalizeOrganizationId(value: any): string | null {
    const raw = (value ?? '').toString().trim();
    if (!raw) return null;
    const withoutQuery = raw.split('?')[0];
    const parts = withoutQuery.split('/').filter(Boolean);
    return parts.length ? parts[parts.length - 1] : null;
  }

  private normalizePractitionerId(value: any): string | null {
    const raw = (value ?? '').toString().trim();
    if (!raw) return null;
    const withoutQuery = raw.split('?')[0];
    const parts = withoutQuery.split('/').filter(Boolean);
    return parts.length ? parts[parts.length - 1] : null;
  }

  private getLoggedInUserRoles(user: any): string[] {
    const fromArray = Array.isArray(user?.roles)
      ? user.roles.map((role: unknown) => String(role ?? '').trim().toLowerCase()).filter(Boolean)
      : [];
    const singleRole = String(user?.role ?? '').trim().toLowerCase();

    return Array.from(new Set([
      ...fromArray,
      ...(singleRole ? [singleRole] : [])
    ]));
  }

  private hasLoggedInRole(user: any, role: string): boolean {
    const normalizedRole = role.trim().toLowerCase();
    return this.getLoggedInUserRoles(user).includes(normalizedRole);
  }

  private hasNonPatientRole(user: any): boolean {
    return this.getLoggedInUserRoles(user).some((role) => role !== 'patient');
  }

  private isPatientOnlyUser(user: any): boolean {
    return this.hasLoggedInRole(user, 'patient') && !this.hasNonPatientRole(user);
  }

  private currentPractitionerReference(): string | null {
    const user = this.authService.user.getValue();
    if (!user || !this.hasNonPatientRole(user)) return null;

    const practitionerId = this.normalizePractitionerId(user?.['fhirResourceId']);
    if (!practitionerId) return null;

    return `Practitioner/${practitionerId}`;
  }

  private filterOutCurrentPractitioner(options: ReferenceDataType[]): ReferenceDataType[] {
    const selfRef = this.currentPractitionerReference();
    if (!selfRef) return options;
    return options.filter(option => String(option.reference ?? '') !== selfRef);
  }

  private ensureSelectedPractitionerStillValid(): void {
    if (!this.selectedPractitionerRef) return;
    const stillPresent = this.practitionerReferenceOptions.some((option) => option.reference === this.selectedPractitionerRef);
    if (!stillPresent) {
      this.selectPractitioner(null);
    }
  }

  protected isHealthcareServiceReference(value: string | null | undefined): boolean {
    return typeof value === 'string' && value.startsWith('HealthcareService/');
  }

  private extractHealthcareServiceId(value: string | null | undefined): string | null {
    if (!this.isHealthcareServiceReference(value || '')) return null;
    const parts = String(value).split('/');
    return parts.pop() || null;
  }

  private updatePractitionerPickerField(options: ReferenceDataType[]): void {
    this.practitionerPickerMeta = {
      formName: '', formDescription: '', showSubmitButton: false,
      closeDialogOnSubmit: false, submitText: ''
    };

    this.practitionerPickerFields = [{
      generalProperties: {
        fieldName: 'Practitioner', fieldApiName: 'consultingPractitioner',
        fieldLabel: 'Practitioner', fieldPlaceholder: 'Search by name...',
        isArray: false, isGroup: false, fieldType: 'IndividualReferenceField',
        validations: [{ type: 'default', name: 'required', isFunction: false }]
      },
      data: options
    } as IndividualReferenceField];
  }

  // private updateShowSchedule(): void {
  //   this.showSchedule = this.bookingType() !== 'Consultation Request' || this.consultationMode === 'consult-appointment';
  // }
  private getPractitionerDisplayName(practitioner: Practitioner, fallbackId: string): string {
    const name = (practitioner?.name ?? [])[0] as any;

    // Try text field first
    if (name?.text?.trim()) return name.text.trim();

    // Build from given + family
    const given = Array.isArray(name?.given)
      ? name.given.map(String).filter(Boolean).join(' ') : '';
    const family = String(name?.family ?? '').trim();
    const combined = `${given} ${family}`.trim();

    return combined || `Practitioner ${fallbackId}`;
  }
  private buildPractitionerListFromSchedules(): void {
    const practitionerIds = new Set<string>();
    for (const schedule of this.activeSchedules) {
      const actors = (schedule.actor ?? []) as Array<{ reference?: string }>;
      for (const actor of actors) {
        const ref = (actor?.reference ?? '').toString().trim();
        if (!ref?.includes('Practitioner/')) continue;
        const id = ref.split('/')[1];
        if (id) practitionerIds.add(id);
      }
    }

    if (practitionerIds.size === 0) {
      this.basePractitionerReferenceOptions = [];
      this.applyPractitionerFilters();
      return;
    }

    const requests = Array.from(practitionerIds).map(id => {
      const ctx = new HttpContext().set(LoadingUIEnabled, false);
      return this.http.get<Practitioner>(
        `${this.backendUrl}/Practitioner/${encodeURIComponent(id)}`,
        { context: ctx }
      ).pipe(
        map(practitioner => ({
          id,
          practitioner,
          ref: `Practitioner/${id}`,
          success: true
        })),
        catchError((error) => {
          // Handle 404 or other errors gracefully - return ID as fallback
          console.warn(`Practitioner ${practitionerIds} not found:`, error);
          return of({
            id,
            practitioner: null,
            ref: `Practitioner/${id}`,
            success: false
          });
        })
      );
    });

    forkJoin(requests).subscribe({
      next: (results) => {
        const options: ReferenceDataType[] = results.map(r => {
          // Helper to map 
          const display = r.success && r.practitioner
            ? this.getPractitionerDisplayName(r.practitioner, r.id)
            : `Practitioner ${r.id}`;

          return { type: 'Practitioner', reference: r.ref, display };
        });

        options.sort((a, b) => String(a.display ?? '').localeCompare(String(b.display ?? '')));
        this.basePractitionerReferenceOptions = options;
        console.log(    this.basePractitionerReferenceOptions, '    this.basePractitionerReferenceOptions ' )
        this.applyPractitionerFilters();
      }
    });
  }

  consultationScopeOptions(): Array<{ value: ConsultationScope; label: string; description: string }> {
    return [
      {
        value: 'same-org',
        label: 'Same hospital',
        description: this.currentOrganizationLabel() ? `Consultants in ${this.currentOrganizationLabel()}` : 'Consultants in your current organization'
      },
      {
        value: 'any-org',
        label: 'Any hospital',
        description: 'Consultants across shared hospitals'
      }
    ];
  }

  onConsultationScopeChange(scope: ConsultationScope): void {
    if (this.consultationScope() === scope) {
      return;
    }

    this.consultationScope.set(scope);
    this.consultationMode = null;
    this.showSchedule = false;
    this.selectedScheduleId = null;
    this.selectedSlot = null;
    this.availableSlots = [];
    this.freeDatesSet.clear();
    this.selectPractitioner(null);

    if (scope === 'same-org') {
      this.fetchSameOrgConsultants();
    } else {
      this.fetchGlobalConsultants();
    }

    this.fetchActiveSchedules();
    this.applyPractitionerFilters();
  }

  isConsultationScopeLoading(): boolean {
    return this.consultationScope() === 'any-org'
      ? this.loadingGlobalConsultants
      : this.loadingSameOrgConsultants;
  }

  consultationPractitionerEmptyMessage(): string {
    if (this.consultationScope() === 'any-org') {
      return 'No consultants were found across shared hospitals.';
    }

    const orgLabel = this.currentOrganizationLabel();
    return orgLabel
      ? `No consultant practitioners were found for ${orgLabel}.`
      : 'No consultant practitioners were found for the current hospital.';
  }

  private currentOrganizationId(): string {
    const currentUser = this.authService.user.getValue();
    return (currentUser?.organizationId ?? currentUser?.hospitalId ?? '').toString().trim();
  }

  currentOrganizationLabel(): string {
    const currentUser = this.authService.user.getValue();
    return (currentUser?.['organizationName'] ?? currentUser?.['hospitalName'] ?? '').toString().trim();
  }

  private consultantReadContext(): HttpContext {
    let context = new HttpContext().set(LoadingUIEnabled, false);
    if (this.isConsultationRequest() && this.consultationScope() === 'any-org') {
      context = context.set(SkipHospitalSegregation, true);
    }
    return context;
  }

  private fetchSameOrgConsultants(): void {
    const orgId = this.normalizeOrganizationId(this.currentOrganizationId());
    if (!orgId || this.loadingSameOrgConsultants) {
      return;
    }

    this.loadingSameOrgConsultants = true;
    const ctx = new HttpContext().set(LoadingUIEnabled, false).set(SkipHospitalSegregation, true);
    const consultantRole = encodeURIComponent(SystemRoles.Consultant);
    const url = `${this.backendUrl}/PractitionerRole?role=${consultantRole}&organization=Organization/${encodeURIComponent(orgId)}&_count=200`;

    this.http.get<Bundle<PractitionerRole>>(url, { context: ctx }).pipe(
      catchError(() => of(null))
    ).subscribe({
      next: (bundle) => {
        this.resolveConsultantOptionsFromRoleBundles([bundle], ctx).subscribe({
          next: (options: ReferenceDataType[]) => {
            this.sameOrgConsultantsOptions = options;
            this.loadingSameOrgConsultants = false;
            this.applyPractitionerFilters();
          },
          error: () => {
            this.sameOrgConsultantsOptions = [];
            this.loadingSameOrgConsultants = false;
            this.applyPractitionerFilters();
          }
        });
      },
      error: (err) => {
        console.error('Failed to fetch same-organization consultants', err);
        this.sameOrgConsultantsOptions = [];
        this.loadingSameOrgConsultants = false;
        this.applyPractitionerFilters();
      }
    });
  }

  private fetchGlobalConsultants(): void {
    if (this.loadingGlobalConsultants) return;
    this.loadingGlobalConsultants = true;

    const ctx = new HttpContext().set(LoadingUIEnabled, false).set(SkipHospitalSegregation, true);
    // Fetch all practitioner roles that are consultants
    const url = `${this.backendUrl}/PractitionerRole?role=${encodeURIComponent(SystemRoles.Consultant)}&_count=200`;

    this.http.get<Bundle<PractitionerRole>>(url, { context: ctx }).subscribe({
      next: (bundle) => {
        this.resolveConsultantOptionsFromRoleBundles([bundle], ctx).subscribe({
          next: (options: ReferenceDataType[]) => {
            this.globalConsultantsOptions = options;
            this.loadingGlobalConsultants = false;
            this.applyPractitionerFilters();
          },
          error: () => {
            this.globalConsultantsOptions = [];
            this.loadingGlobalConsultants = false;
            this.applyPractitionerFilters();
          }
        });

      },
      error: (err) => {
        console.error('Failed to fetch global consultants', err);
        this.globalConsultantsOptions = [];
        this.loadingGlobalConsultants = false;
        this.applyPractitionerFilters();
      }
    });
  }

  private resolveConsultantOptionsFromRoleBundles(
    bundles: Array<Bundle<PractitionerRole> | null>,
    context: HttpContext
  ): Observable<ReferenceDataType[]> {
    const practitionerIds = new Set<string>();
    const organizationByPractitioner = new Map<string, ConsultantOrganizationInfo>();

    for (const bundle of bundles) {
      for (const entry of bundle?.entry ?? []) {
        const role = entry?.resource as PractitionerRole | undefined;
        const ref = (role?.practitioner?.reference ?? '').toString().trim();
        if (!ref.includes('Practitioner/')) {
          continue;
        }

        const id = ref.split('/')[1];
        if (id) {
          practitionerIds.add(id);
          if (!organizationByPractitioner.has(id)) {
            const organizationReference = role?.organization?.reference?.trim();
            const normalizedOrganizationId = this.normalizeOrganizationId(organizationReference) ?? undefined;
            organizationByPractitioner.set(id, {
              organizationId: normalizedOrganizationId,
              organizationReference: organizationReference || undefined,
              organizationDisplay: role?.organization?.display?.trim() || undefined
            });
          }
        }
      }
    }

    if (!practitionerIds.size) {
      return of([] as ReferenceDataType[]);
    }

    const requests = Array.from(practitionerIds).map(id => {
      return this.http.get<Practitioner>(
        `${this.backendUrl}/Practitioner/${encodeURIComponent(id)}`,
        { context }
      ).pipe(
        map(practitioner => ({
          id,
          practitioner,
          ref: `Practitioner/${id}`,
          success: true
        })),
        catchError(() => of({
          id,
          practitioner: null,
          ref: `Practitioner/${id}`,
          success: false
        }))
      );
    });

    return forkJoin(requests).pipe(
      map(results => results
        .map(result => ({
          type: 'Practitioner' as const,
          reference: result.ref,
          display: result.success && result.practitioner
            ? this.getPractitionerDisplayName(result.practitioner, result.id)
            : `Consultant ${result.id}`,
          organizationId: organizationByPractitioner.get(result.id)?.organizationId,
          organizationReference: organizationByPractitioner.get(result.id)?.organizationReference,
          organizationDisplay: organizationByPractitioner.get(result.id)?.organizationDisplay
        }))
        .sort((a, b) => String(a.display ?? '').localeCompare(String(b.display ?? ''))))
    );
  }

  private getSelectedConsultantOption(practitionerRef: string): ReferenceDataType | undefined {
    return this.practitionerReferenceOptions.find(option => String(option.reference ?? '') === practitionerRef);
  }

   private buildCrossOrganizationConsultationInput(
    practitionerRef: string | null,
    requesterRef: string
  ): ReferralWorkflowInput | null {
    if (!practitionerRef) return null;
    const selectedOption = this.getSelectedConsultantOption(practitionerRef);
    const destinationOrganizationId = this.normalizeOrganizationId(
      selectedOption?.['organizationId'] || selectedOption?.['organizationReference']
    );
    const sourceOrganizationId = this.normalizeOrganizationId(this.currentOrganizationId());
    const patient = this.state.PatientResources.currentPatient.getValue()?.actualResource;
    const encounter = this.state.currentEncounter.getValue();

    if (!patient?.id || !destinationOrganizationId) {
      return null;
    }

    if (sourceOrganizationId && destinationOrganizationId === sourceOrganizationId) {
      return null;
    }

    const observations = this.state.PatientResources.observations.getValue()
      .map(entry => entry.actualResource)
      .filter(resource => this.state.isResourceForCurrentEncounter(resource));
    const conditions = this.state.PatientResources.condition.getValue()
      .map(entry => entry.actualResource);
    const medicationRequests = this.state.PatientResources.medicationRequests.getValue()
      .map(entry => entry.actualResource);
    const diagnosticReports = this.state.PatientResources.diagnosticReports.getValue()
      .map(entry => entry.actualResource)
      .filter(resource => this.state.isResourceForCurrentEncounter(resource));
    const serviceRequests = this.state.PatientResources.serviceRequests.getValue()
      .map(entry => entry.actualResource)
      .filter((resource): resource is any => !!resource?.resourceType && resource.resourceType === 'ServiceRequest')
      .filter(resource => this.state.isResourceForCurrentEncounter(resource));

    const consultationNote = this.consultationNoteText();

    return {
      title: 'Cross-Organization Consultation Request',
      note: [
        consultationNote,
        selectedOption?.display
          ? `Consultation requested with ${selectedOption.display}. Review the shared document before opening the destination chart.`
          : 'Cross-organization consultation request.'
      ].filter(Boolean).join('\n\n'),
      reasonText: 'Cross-organization specialist consultation',
      requesterReference: requesterRef,
      requesterDisplay: this.currentOrganizationLabel() || undefined,
      serviceRequestCategoryCode: 'consultation',
      serviceRequestCategoryDisplay: 'Consultation',
      serviceRequestCodeCode: 'consultation',
      serviceRequestCodeDisplay: 'Consultation',
      performerReferences: [
        {
          reference: practitionerRef,
          display: selectedOption?.display
        },
        {
          reference: `Organization/${destinationOrganizationId}`,
          display: selectedOption?.['organizationDisplay']
        }
      ],
      createDestinationPatientShell: false,
      source: {
        patient,
        encounter: encounter || undefined,
        observations,
        conditions,
        diagnosticReports,
        medicationRequests,
        allergies: this.state.PatientResources.allergyIntolerances.getValue().map(entry => entry.actualResource),
        immunizations: this.state.PatientResources.immunizations.getValue().map(entry => entry.actualResource),
        serviceRequests
      },
      destination: {
        organizationId: destinationOrganizationId,
        organizationDisplay: selectedOption?.['organizationDisplay'],
        practitionerReference: practitionerRef,
        practitionerDisplay: selectedOption?.display
      }
    };
  }

  private sendCrossOrgConsultationReferral(
    workflowInput: ReferralWorkflowInput
  ): Observable<ReferralWorkflowResult> {
    return this.referralWorkflowService.createReferral(workflowInput);
  }

  route = inject(ActivatedRoute);
  // bookingType = model<string>('Consultation Request');
  // dialog = inject(MatDialog);
  // showSchedule = true;
  // consultationMode: 'none' | 'consult-only' | 'consult-appointment' = 'none';

  constructor() {
    // metadata Effect to handle bookingType changes if needed? 
    // For now, simpler to check in onPractitionerPickerChange and init
  }

  ngOnChanges() {
    this.updateShowSchedule();
  }

  // Since using model(), we might want an effect or just check value
  updateShowSchedule() {
    if (this.bookingType() === 'Consultation Request') {
      this.showSchedule = false;
    } else {
      this.showSchedule = true;
    }
  }

  onPractitionerPickerChange(value: any): void {
    const raw = value?.consultingPractitioner;
    // Dynamic form emits on every keystroke; only react when a real reference object/value is selected.
    if (raw === null || raw === undefined || raw === '') {
      if (this.selectedPractitionerRef) this.selectPractitioner(null);
      return;
    }

    const ref = this.coercePractitionerReference(raw);
    if (!ref) return;

    if (ref === this.selectedPractitionerRef) return;

    // Check booking type
    if (this.bookingType() === 'Consultation Request') {
      // Open dialog
      const option = this.practitionerReferenceOptions.find(o => o.reference === ref);
      const practitionerName = option?.display || 'the practitioner';

      const dialogRef = this.dialog.open(ConsultationConfirmationDialogComponent, {
        data: { practitionerName },
        width: '500px'
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result === 'consult-only') {
          this.createConsultOnlyRequest(ref);
          // Do not select practitioner in UI to show schedule? 
          // Or maybe select but keep schedule hidden?
          // Requirement: "for he one of consult only -> a service rques is mde... "
          // User didn't say what to do with UI after. Assuming we stay there or show success.
        } else if (result === 'consult-appointment') {
          this.consultationMode = 'consult-appointment';
          this.showSchedule = true;
          this.selectPractitioner(ref);
        } else {
          // Cancelled
          // Reset picker? hard to reset dynamic form from here without rebuilding
          // For now, just don't select
          this.selectPractitioner(null);
        }
      });

    } else {
      this.selectPractitioner(ref);
    }
  }

  private coercePractitionerReference(raw: any): string | null {
    if (!raw) return null;
    // Selected option is usually a ReferenceDataType-like object.
    if (typeof raw === 'object' && raw.reference) {
      const r = String(raw.reference).trim();
      return r.includes('Practitioner/') ? r : null;
    }
    // Fallback: sometimes fields store an encoded string; accept Practitioner/{id} prefix.
    if (typeof raw === 'string') {
      const s = raw.trim();
      if (!s) return null;
      const head = s.split('$#$')[0] ?? s;
      return head.includes('Practitioner/') ? head : null;
    }
    return null;
  }

  selectPractitioner(practitionerRef: string | null): void {
    this.selectedPractitionerRef = practitionerRef;
    this.selectedScheduleId = null; // Reset schedule chip selection when practitioner changes
    this.selectedSlot = null;
    this.availableSlots = [];

    this.selectedPractitioner = null;
    this.selectedPractitionerPhone = null;
    this.selectedPractitionerEmail = null;

    if (practitionerRef) {
      this.fetchAndHighlightFreeSlots(practitionerRef, null);
    } else {
      this.freeDatesSet.clear();
      this.refreshCalendarHighlights();
    }

    if (practitionerRef) {
      const id = practitionerRef.split('/').pop() ?? '';
      if (id) {
        const ctx = this.consultantReadContext();
        this.http.get<Practitioner>(`${this.backendUrl}/Practitioner/${encodeURIComponent(id)}`, { context: ctx }).subscribe({
          next: (p) => {
            this.selectedPractitioner = p;
            const telecom = (p?.telecom ?? []) as Array<{ system?: string; value?: string }>;
            const phone = telecom.find((t) => String(t?.system ?? '').toLowerCase() === 'phone')?.value ?? null;
            const email = telecom.find((t) => String(t?.system ?? '').toLowerCase() === 'email')?.value ?? null;
            this.selectedPractitionerPhone = phone ? String(phone) : null;
            this.selectedPractitionerEmail = email ? String(email) : null;
          },
          error: () => {
            // Best-effort; leave details blank.
          }
        });
      }
    }

    const date = this.selected();
    if (date && practitionerRef) {
      this.fetchSlotsForDate(date);
    }
  }

  onDateChange(date: Date | null) {
    if (!date) return;

    if (this.selectedPractitionerRef && this.freeDatesSet.size > 0) {
      const localIso = date.toLocaleDateString('en-CA');
      if (!this.freeDatesSet.has(localIso)) {
        this.sn.open('No explicitly free slots available on this date.', 'Close', { duration: 2500 });
        return;
      }
    }

    this.selected.set(date);
    if (this.selectedPractitionerRef) {
      this.fetchSlotsForDate(date);
    } else {
      this.availableSlots = [];
      this.selectedSlot = null;
    }
  }

  private fetchAndHighlightFreeSlots(practitionerRef: string, scheduleId: string | null) {
    this.freeDatesSet.clear();
    // Default to today
    this.calendarStartAt = new Date();

    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    const startStr = startOfDay.toISOString();

    let scheduleIds: string[];
    if (scheduleId) {
      scheduleIds = [scheduleId];
    } else {
      scheduleIds = this.activeSchedules
        .filter((s) => {
          const actors = (s.actor ?? []) as Array<{ reference?: string }>;
          return actors.some((a) => String(a?.reference ?? '') === practitionerRef);
        })
        .map((s) => (s?.id ?? '').toString().trim())
        .filter(Boolean);
    }

    if (scheduleIds.length === 0) {
      this.refreshCalendarHighlights();
      return;
    }

    const requests = scheduleIds.map((id) => {
      const url = `${this.backendUrl}/Slot?schedule=Schedule/${encodeURIComponent(id)}&start=ge${startStr}&status=free&_count=500`;
      return this.fetchAllSlotPages(url).pipe(
        catchError(() => of([] as Slot[]))
      );
    });

    forkJoin(requests).subscribe({
      next: (slotLists) => {
        const slots = slotLists.flat();
        let earliestDateMs = Infinity;

        for (const slot of slots) {
          if (!slot.start) continue;
          const d = new Date(slot.start);
          if (isNaN(d.getTime())) continue;

          const localIso = d.toLocaleDateString('en-CA');
          this.freeDatesSet.add(localIso);

          if (d.getTime() < earliestDateMs && d.getTime() >= startOfDay.getTime()) {
            earliestDateMs = d.getTime();
          }
        }

        if (earliestDateMs !== Infinity) {
          this.calendarStartAt = new Date(earliestDateMs);
        }

        this.refreshCalendarHighlights();
      },
      error: () => { }
    });
  }

  fetchSlotsForDate(date: Date) {
    if (!this.selectedPractitionerRef) return;

    this.loadingSlots = true;
    this.selectedSlot = null;
    this.availableSlots = [];

    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    // Fetch slots that overlap with this day
    // FHIR search: start=ge[startOfDay]&start=le[endOfDay]
    const startStr = startOfDay.toISOString();
    const endStr = endOfDay.toISOString();

    // If a specific schedule chip is selected, use only that schedule; otherwise use all for practitioner.
    let scheduleIds: string[];
    if (this.selectedScheduleId) {
      scheduleIds = [this.selectedScheduleId];
    } else {
      scheduleIds = this.activeSchedules
        .filter((s) => {
          const actors = (s.actor ?? []) as Array<{ reference?: string }>;
          return actors.some((a) => String(a?.reference ?? '') === this.selectedPractitionerRef);
        })
        .map((s) => (s?.id ?? '').toString().trim())
        .filter(Boolean);
    }

    if (scheduleIds.length === 0) {
      this.loadingSlots = false;
      return;
    }

    const requests = scheduleIds.map((id) => {
      const url = `${this.backendUrl}/Slot?schedule=Schedule/${encodeURIComponent(id)}&start=ge${startStr}&start=le${endStr}&status=free&_count=200`;
      return this.fetchAllSlotPages(url);
    });

    forkJoin(requests.length ? requests : [of([] as Slot[])]).subscribe({
      next: (slotLists) => {
        const slots = slotLists.flat();
        this.processSlots(slots);
        this.loadingSlots = false;
      },
      error: (err) => {
        console.error('Failed to fetch slots', err);
        this.loadingSlots = false;
      }
    });
  }

  private fetchAllSlotPages(initialUrl: string, maxPages = 100): Observable<Slot[]> {
    const context = this.consultantReadContext();

    return this.http.get<Bundle<Slot>>(initialUrl, { context }).pipe(
      expand((bundle, pageIndex) => {
        if (pageIndex + 1 >= maxPages) return EMPTY;

        const nextLink = (bundle?.link ?? []).find(
          (link) => String(link?.relation ?? '').toLowerCase() === 'next'
        )?.url;
        const nextUrl = this.normalizeSlotNextLink(nextLink);

        return nextUrl
          ? this.http.get<Bundle<Slot>>(nextUrl, { context })
          : EMPTY;
      }),
      map((bundle) => (bundle?.entry ?? []).map((entry) => entry.resource as Slot).filter(Boolean)),
      reduce((slots, pageSlots) => slots.concat(pageSlots), [] as Slot[])
    );
  }

  private normalizeSlotNextLink(nextLink: string | undefined): string | null {
    const value = String(nextLink ?? '').trim();
    if (!value) return null;

    try {
      const nextUrl = new URL(value, `${this.backendUrl.replace(/\/$/, '')}/`);
      const slotPathIndex = nextUrl.pathname.lastIndexOf('/Slot');
      if (slotPathIndex < 0) return value;

      const backendBase = this.backendUrl.replace(/\/$/, '');
      return `${backendBase}${nextUrl.pathname.slice(slotPathIndex)}${nextUrl.search}${nextUrl.hash}`;
    } catch {
      return value;
    }
  }

  processSlots(slots: Slot[]) {
    const selectedRef = this.selectedPractitionerRef ?? '';
    const practitionerName = this.getSelectedPractitionerDisplayName();
    const practitionerRef = selectedRef;

    // Deduplicate slots by ID
    const seenIds = new Set<string>();
    const uniqueSlots = slots.filter(slot => {
      if (!slot.id) return true;
      if (seenIds.has(slot.id)) return false;
      seenIds.add(slot.id);
      return true;
    });

    this.availableSlots = uniqueSlots
      .filter(slot => {
        // ✅ IMPORTANT: Only show slots with valid start/end times
        if (!slot.start || !slot.end) return false;

        const start = new Date(slot.start);
        const end = new Date(slot.end);

        // ✅ Check if dates are valid (not NaN)
        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
          console.warn('Invalid slot dates:', slot.start, slot.end);
          return false;
        }

        return true;
      })
      .map(slot => {
        const start = new Date(slot.start!);
        const end = new Date(slot.end!);

        // Format time properly
        const timeDisplay = `${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;

        return {
          slot,
          practitionerName,
          timeDisplay,
          practitionerRef
        };
      });

    // Sort by slot start time
    this.availableSlots.sort((a, b) => {
      const aMs = new Date(a.slot.start ?? '').getTime();
      const bMs = new Date(b.slot.start ?? '').getTime();
      return aMs - bMs;
    });
  }

  private getSelectedPractitionerDisplayName(): string {
    // Prefer the resolved Practitioner resource (gives best display)
    const p = this.selectedPractitioner;
    const name = (p?.name ?? [])[0] as any;
    const text = String(name?.text ?? '').trim();
    if (text) return text;
    const given = Array.isArray(name?.given) ? name.given.map((g: any) => String(g)).filter(Boolean).join(' ') : '';
    const family = String(name?.family ?? '').trim();
    const combined = `${given} ${family}`.trim();
    if (combined) return combined;

    // Fallback to schedule-provided display for the selected reference
    const ref = this.selectedPractitionerRef;
    const option = this.practitionerReferenceOptions.find((o) => String(o.reference ?? '') === String(ref ?? ''));
    return String(option?.display ?? 'Practitioner').trim() || 'Practitioner';
  }

  selectSlot(slotItem: AvailableSlotItem) {
    this.selectedSlot = slotItem;
  }

  onScheduleSelected(scheduleId: string | null): void {
    this.selectedScheduleId = scheduleId;
    this.selectedSlot = null;
    this.availableSlots = [];

    if (this.selectedPractitionerRef) {
      this.fetchAndHighlightFreeSlots(this.selectedPractitionerRef, scheduleId);
    }

    // Refetch slots for the currently selected date if one is already chosen
    const date = this.selected();
    if (date && this.selectedPractitionerRef) {
      this.fetchSlotsForDate(date);
    }
  }

  createConsultOnlyRequest(practitionerRef: string) {
    const patientId = this.getEffectivePatientId();
    if (!patientId) {
      this.sn.open('No patient currently selected.', 'Close', { duration: 3000 });
      return;
    }

    // Get requester
    const currentUser = this.authService.user.getValue();
    let requesterRef = `Patient/${patientId}`; // Fallback?
    if (currentUser) {
      if (this.isPatientOnlyUser(currentUser)) {
        requesterRef = `Patient/${this.normalizePatientId(currentUser['fhirResourceId']) || patientId}`;
      } else {
        requesterRef = `Practitioner/${currentUser['fhirResourceId'] || currentUser['fhirResourceId']}`;
      }
    }

    // Encounter
    const encounter = this.state.currentEncounter.getValue();
    const encounterId = encounter?.id;

    if (!encounterId) {
      this.sn.open('No active encounter found.', 'Close', { duration: 3000 });
      return;
    }

    const crossOrgReferralInput = this.consultationScope() === 'any-org'
      ? this.buildCrossOrganizationConsultationInput(practitionerRef, requesterRef)
      : null;

    if (crossOrgReferralInput) {
      this.sendCrossOrgConsultationReferral(crossOrgReferralInput).subscribe({
        next: () => {
          this.sn.openFromComponent(SuccessMessageComponent, {
            data: { message: 'Cross-hospital consultation document shared successfully.' },
            duration: 3000
          });
          setTimeout(() => {
            this.dialogRef?.close({ success: true, mode: 'cross-org-consultation' });
          }, 1200);
        },
        error: (err) => {
          console.error(err);
          this.sn.open('Failed to send cross-hospital consultation package.', 'Close', { duration: 3500 });
        }
      });
      return;
    }

    if (this.consultationScope() === 'any-org' && !crossOrgReferralInput) {
      this.sn.open('Consultant is in your hospital—sending an internal consultation request instead.', 'Close', { duration: 3000 });
    }

    this.appointmentService.createServiceRequest(
      practitionerRef,
      patientId,
      encounterId,
      requesterRef,
      this.consultationNoteText()
    ).subscribe({
      next: () => {
        this.sn.openFromComponent(SuccessMessageComponent, {
          data: { message: 'Consultation request sent successfully!' },
          duration: 3000
        });
      },
      error: (err) => {
        console.error(err);
        this.sn.open('Failed to send consultation request.', 'Close', { duration: 3000 });
      }
    });
  }

  sendRequest() {
    if (!this.isWaitlist() && !this.selectedSlot) return;

    const patientId = this.getEffectivePatientId();
    if (!patientId) {
      this.sn.open('No patient currently selected in context', 'Close', { duration: 3000 });
      return;
    }

    const slot = this.selectedSlot?.slot || null;
    const practitionerRef = this.selectedSlot?.practitionerRef || null;
    const currentUser = this.authService.user.getValue();
    const otherParticipants: any[] = [];
    let requesterRef = `Patient/${patientId}`;
    const requestingPractitionerRef = this.currentPractitionerReference();

    if (currentUser) {
      let userRef = '';
      if ((currentUser.role ?? '').toString().toLowerCase() === 'patient') {
        const normalizedPatientId = this.normalizePatientId(currentUser['fhirResourceId']) || patientId;
        userRef = `Patient/${normalizedPatientId}`;
      } else if (requestingPractitionerRef) {
        userRef = requestingPractitionerRef;
      } else {
        const fallbackId = this.normalizePractitionerId(currentUser['fhirResourceId']);
        userRef = fallbackId ? `Practitioner/${fallbackId}` : '';
      }

      requesterRef = userRef || requesterRef;

      if (userRef && userRef !== `Patient/${patientId}` && userRef !== practitionerRef) {
        otherParticipants.push({
          actorReference: userRef,
          status: 'accepted',
          type: 'referrer'
        });
      }
    }
    if (this.assignMode() && this.existingAppointment()) {
      this.handleAppointmentUpdate();
      return;
    }

    console.log('🚀 About to submit appointment request');
    console.log('👤 Current user:', currentUser);
    console.log('🆔 User id:', currentUser?.['id']);
    console.log('🆔 User userId:', currentUser?.['userId']);
    console.log('🆔 User fhirResourceId:', currentUser?.['fhirResourceId']);
    console.log('🏥 Patient ID:', patientId);
    console.log('👨‍⚕️ Practitioner ref:', practitionerRef);
    console.log('📋 Requester ref:', requesterRef);
    const practitionerOnlyAppointment = this.bookingType() === 'Consultation Request'
      && this.consultationMode === 'consult-appointment'
      && !!requestingPractitionerRef
      && requestingPractitionerRef !== practitionerRef;

    if (this.consultationMode === 'consult-appointment') {
      const encounter = this.state.currentEncounter.getValue();
      const encounterId = encounter?.id;

       const isVirtual = this.deliveryMode() === 'virtual';
      // const isVirtual = isTelemedicine;
      // const appointmentTypeCoding = isTelemedicine
      //   ? { system: 'http://terminology.hl7.org/CodeSystem/v2-0276', code: 'TELE', display: 'Telemedicine' }
      //   : { system: 'http://terminology.hl7.org/CodeSystem/v2-0276', code: 'CON', display: 'Consultation' };

      const selectedServiceObj = this.serviceOptions.find(o => o.value === this.selectedService());
      const specialtyCode = selectedServiceObj?.specialtyCode;
      const specialtyDisplay = selectedServiceObj?.primarySpecialtyName;

      const isPatientOrigin = this.isPatientOnlyUser(currentUser);

      if (!encounterId) {
        this.sn.open('No active encounter found.', 'Close', { duration: 3000 });
        return;
      }

      const appointment$ = this.appointmentService.createConsultationWithAppointment(
        slot,
        patientId,
        practitionerRef,
        encounterId,
        requesterRef,
        otherParticipants,
        this.bookingType(),
        isVirtual && isPatientOrigin,
        undefined, // appointmentTypeCoding
        undefined, // serviceTypeCoding, if any
        requestingPractitionerRef,
        practitionerOnlyAppointment,
        this.consultationNoteText(),
        {
          requestedPeriodStart: this.getRequestedPeriodStart(),
          requestedPeriodEnd: this.getRequestedPeriodEnd(),
          minutesDuration: this.minutesDuration(),
          patientInstruction: this.patientInstruction(),
          isVirtual: isVirtual,
          specialtyCode,
          specialtyDisplay
        }
      );

      const crossOrgReferralInput = (this.consultationScope() === 'any-org' && practitionerRef)
        ? this.buildCrossOrganizationConsultationInput(practitionerRef, requesterRef)
        : null;

      if (crossOrgReferralInput) {
        this.sendCrossOrgConsultationReferral(crossOrgReferralInput).pipe(
          switchMap(() => appointment$)
        ).subscribe({
          next: () => {
            this.sn.openFromComponent(SuccessMessageComponent, {
              data: { message: 'Cross-hospital consultation and appointment booked successfully.' },
              duration: 3000
            });
            setTimeout(() => {
              this.dialogRef?.close({ success: true, mode: 'cross-org-consultation' });
            }, 1200);
          },
          error: (err: unknown) => {
            console.error(err);
            this.sn.open('Failed to book cross-hospital consultation & appointment.', 'Close', { duration: 3500 });
          }
        });
        return;
      }

      if (this.consultationScope() === 'any-org' && !crossOrgReferralInput) {
        this.sn.open('Consultant is in your hospital—skipping cross-hospital referral.', 'Close', { duration: 3000 });
      }

      appointment$.subscribe({
        next: (_result) => {
          this.sn.openFromComponent(SuccessMessageComponent, {
            data: { message: 'Consultation & Appointment booked successfully!' },
            duration: 3000
          });
          setTimeout(() => {
            this.dialogRef?.close({ success: true });
          }, 1500);
        },
        error: (err: unknown) => {
          console.error(err);
          this.sn.open('Failed to book consultation & appointment.', 'Close', { duration: 3000 });
        }
      });

    } else {
      const isPatientOrigin = this.isPatientOnlyUser(currentUser);
      const isVirtual = this.deliveryMode() === 'virtual';
      const appointmentTypeCoding = isVirtual
        ? { system: 'http://terminology.hl7.org/CodeSystem/v2-0276', code: 'TELE', display: 'Telemedicine' }
        : { system: 'http://terminology.hl7.org/CodeSystem/v2-0276', code: 'ROUTINE', display: 'Routine' };

      // Optional Service Category extraction
      const selectedServiceObj = this.serviceOptions.find(o => o.value === this.selectedService());
      const specialtyCode = selectedServiceObj?.specialtyCode;
      const specialtyDisplay = selectedServiceObj?.primarySpecialtyName;

      let serviceTypeCoding: any = undefined;
      const telemedService = this.selectedService();
      if (telemedService && telemedService !== this.defaultServiceOption && telemedService !== this.otherServiceOption) {
        // We might map specific healthcare services to a serviceType coding if possible here, but omitting for now unless easily obtainable
      }

      this.appointmentService.createAppointment(
        slot,
        patientId,
        practitionerRef,
        true,
        otherParticipants,
        isVirtual && isPatientOrigin,
        appointmentTypeCoding,
        serviceTypeCoding,
        [], // basedOn
        undefined, // requestingPractitionerRef
        false, // practitionerOnly
        this.getRequestedPeriodStart(),
        this.getRequestedPeriodEnd(),
        this.minutesDuration(),
        this.patientInstruction(),
        isVirtual,
        specialtyCode,
        specialtyDisplay
      ).subscribe({
        next: (appt) => {
          this.sn.openFromComponent(SuccessMessageComponent, {
            data: { message: 'Appointment booked successfully!' },
            duration: 3000
          });
          setTimeout(() => {
            this.dialogRef?.close({ appointmentId: appt.appointmentId, success: true });
          }, 1500);
        },
        error: (err) => {
          console.error(err);
          if (err.status === 409) {
            this.sn.open('Cannot book: Patient already has an appointment at this time', 'Close', { duration: 3000 });
          } else {
            this.sn.open('Failed to send request', 'Close', { duration: 3000 });
          }
        }
      });
    }
  }

  handleAppointmentUpdate() {
    const appt = this.existingAppointment();
    const slot = this.selectedSlot?.slot;
    const practitionerRef = this.selectedSlot?.practitionerRef;

    if (!appt?.id || !slot || !practitionerRef) {
      this.sn.open('Missing required information for update.', 'Close', { duration: 3000 });
      return;
    }

    this.appointmentService.updateAppointment(
      appt.id,
      slot,
      undefined, // status
      undefined, // description
      undefined, // topic
      'Assigned practitioner and slot',
      true, // updateZoom
      true, // notify
      undefined, // zoomPassword
      practitionerRef
    ).subscribe({
      next: () => {
        this.sn.openFromComponent(SuccessMessageComponent, {
          data: { message: 'Appointment assigned successfully!' },
          duration: 3000
        });
        setTimeout(() => this.dialogRef?.close({ success: true }), 1500);
      },
      error: (err) => {
        console.error(err);
        this.sn.open('Failed to update appointment.', 'Close', { duration: 3000 });
      }
    });
  }

  consultationNoteText(): string {
    return this.consultationNote().trim();
  }
  dialogRef = inject(MatDialogRef<BookingsFormComponent>, { optional: true });
}
